import { useState, useEffect } from 'react';
import { Loader2, Mail, ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const SCOPE_SHORT_NAMES = {
  'https://www.googleapis.com/auth/gmail.modify': 'Gmail',
  'https://www.googleapis.com/auth/drive': 'Drive',
};

function shortScopes(scopes) {
  return (scopes || []).map(s => SCOPE_SHORT_NAMES[s] || s).join(', ') || 'none';
}

export function GoogleConnectionHealthPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await diagnosticsAPI.googleConnectionHealth();
      setData(d);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const allHealthy = data?.all_healthy;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Google Connection Health</CardTitle>
          {!loading && data && (
            <Badge
              variant={allHealthy ? 'default' : 'destructive'}
              className={`text-[10px] ml-auto ${allHealthy ? 'bg-green-100 text-green-800 hover:bg-green-100' : ''}`}
              data-testid="google-connection-health-badge"
            >
              {allHealthy ? 'ALL HEALTHY' : 'ISSUES FOUND'}
            </Badge>
          )}
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Stored granted scopes plus a live one-call Gmail API probe per account, so a
          scan-failure code (e.g. gmail_api_not_configured) can be root-caused from this
          page instead of guessed at.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground">No data</p>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left px-3 py-1.5 font-medium">Account</th>
                  <th className="text-left px-3 py-1.5 font-medium">Granted scopes</th>
                  <th className="text-left px-3 py-1.5 font-medium">Live probe</th>
                </tr>
              </thead>
              <tbody>
                {data.accounts.map((a) => (
                  <tr key={a.account_id} className={`border-t ${a.connected && !a.live_probe?.ok ? 'bg-red-50' : ''}`} data-testid={`gch-row-${a.account_id}`}>
                    <td className="px-3 py-1.5 font-mono">{a.email}</td>
                    <td className="px-3 py-1.5">
                      {!a.connected ? '—' : a.scopes_known ? shortScopes(a.granted_scopes) : 'unknown (never re-consented)'}
                    </td>
                    <td className="px-3 py-1.5">
                      {!a.connected ? (
                        <span className="text-muted-foreground">not connected</span>
                      ) : a.live_probe?.ok ? (
                        <span className="inline-flex items-center gap-1 text-green-700">
                          <ShieldCheck className="h-3 w-3" /> ok
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-700" title={a.live_probe?.detail}>
                          <ShieldAlert className="h-3 w-3" /> {a.live_probe?.reason}: {a.live_probe?.detail}
                          {a.live_probe?.extended_help_url && (
                            <a href={a.live_probe.extended_help_url} target="_blank" rel="noreferrer" className="underline ml-1">
                              fix in Cloud Console
                            </a>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
